import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Eye, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BackButton } from "@/components/BackButton/BackButton";
import { Badge } from "@/components/ui/badge";
import ResourceDetailsContentBlock from "@/modules/hub/features/ResourceLibrary/components/ResourceDetailsContentBlock/ResourceDetailsContentBlock";
import ResourceDetailsSkeleton from "../ResourceDetailsSkeleton/ResourceDetailsSkeleton";
import { FavoriteButton } from "@/modules/hub/features/ResourceLibrary/components/FavoriteButton/FavoriteButton";
import { ShareToEmployeeButton } from "@/modules/hub/features/ResourceLibrary/components/ShareToEmployeeButton/ShareToEmployeeButton";
import { updateResourceViewCount } from "@/api/updateResourceViewCount/updateResourceViewCountApi";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { getResourceDetails } from "@/modules/hub/features/ResourceLibrary/api/getResourceDetails/getResourceDetailsApi";
import { GetResourceDetailsResponse } from "@/modules/hub/features/ResourceLibrary/api/getResourceDetails/types";
import { VideoEmbed } from "@/modules/hub/features/ResourceLibrary/components/VideoEmbed/VideoEmbed";
import { PodcastEmbed } from "@/modules/hub/features/ResourceLibrary/components/PodcastEmbed/PodcastEmbed";
import { FileEmbed } from "@/modules/hub/features/ResourceLibrary/components/FileEmbed/FileEmbed";
import { ExternalLink } from "lucide-react";
import ShowIfHasAccess from "@/components/ShowIfHasAccess/ShowIfHasAccess";
import { trackPodcastDetailsView } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import { trackNewsDetailsView } from "@/modules/hub/features/News/analytics/newsAnalytics";
import { trackResourceDetailsView } from "@/modules/hub/features/ResourceLibrary/analytics/resourceLibraryAnalytics";

export default function ResourceDetails() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [resourceNotFound, setResourceNotFound] = useState<boolean>(false);
  const [resource, setResource] = useState<
    GetResourceDetailsResponse["data"] | null
  >(null);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const trackedResourceUuid = useRef<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!slug) return;
      setLoading(true);
      setResourceNotFound(false);

      try {
        const response = await getResourceDetails(slug);
        const resourceData = response.data;

        if (!resourceData || resourceData.id === null) {
          setResourceNotFound(true);
        } else {
          // Check if this is a Partner Page that should redirect
          if (
            resourceData.typeName === "Partner Page" &&
            resourceData.contentUrl
          ) {
            window.open(
              resourceData.contentUrl,
              "_blank",
              "noopener,noreferrer",
            );
            navigate("/hub/resources");
            return;
          }
          setResource(resourceData);
          setIsFavorited(resourceData.isFavorited);
        }
      } catch (error) {
        console.error("Failed to fetch resource:", error);
        setResourceNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [slug, navigate]);

  const incrementViewCount = async () => {
    if (!resource?.uuid) return;
    try {
      await updateResourceViewCount({ resourceUuid: resource.uuid });
    } catch (error) {
      console.error("Error incrementing view count:", error);
    }
  };

  useEffect(() => {
    if (resource && !loading) {
      const timer = setTimeout(() => {
        incrementViewCount();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resource, loading]);

  useEffect(() => {
    if (
      resource?.uuid &&
      resource.typeName &&
      trackedResourceUuid.current !== resource.uuid
    ) {
      if (resource.typeName === "Podcast") {
        trackPodcastDetailsView({
          resourceType: resource.typeName,
          resourceUuid: resource.uuid,
        });
        trackedResourceUuid.current = resource.uuid;
      } else if (resource.typeName === "News") {
        trackNewsDetailsView({
          resourceType: resource.typeName,
          resourceUuid: resource.uuid,
        });
        trackedResourceUuid.current = resource.uuid;
      } else {
        trackResourceDetailsView({
          resourceType: resource.typeName,
          resourceUuid: resource.uuid,
        });
        trackedResourceUuid.current = resource.uuid;
      }
    }
  }, [resource?.uuid, resource?.typeName]);

  const handleToggleFavorite = async () => {
    if (!resource?.uuid) return;
    try {
      const response = await toggleResourceFavorite({
        resourceUuid: resource.uuid,
      });
      setIsFavorited(response.data.favorited);
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  if (resourceNotFound) {
    return (
      <MainPageWrapper hideHeader title="Resource Library">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Resource Not Found</h1>
          <p className="mb-6">
            The resource you&apos;re looking for doesn&apos;t exist or has been
            removed.
          </p>
          <BackButton />
        </div>
      </MainPageWrapper>
    );
  }

  if (loading) {
    return (
      <MainPageWrapper hideHeader title="Resource Library">
        <ResourceDetailsSkeleton />
      </MainPageWrapper>
    );
  }

  if (!resource) {
    return (
      <MainPageWrapper hideHeader title="Resource Library">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
            <h1 className="text-2xl font-bold mb-4">Resource Not Found</h1>
            <p className="mb-6">
              The resource you&apos;re looking for doesn&apos;t exist or has
              been removed.
            </p>
            <BackButton />
          </div>
        </div>
      </MainPageWrapper>
    );
  }

  const {
    title,
    thumbnailUrl,
    contentUrl,
    typeName,
    viewCount,
    publishStartDate,
    categories,
    trades,
    roles,
    contentBlocks,
    icon,
    backgroundColor,
    textColor,
    borderColor,
    relatedResources,
  } = resource;

  return (
    <MainPageWrapper hideHeader title="Resource Details">
      <div>
        <div className="mb-6">
          <BackButton />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              {/* Hero Image Section - Don't show for Video type since video embed has its own thumbnail */}
              {thumbnailUrl && typeName !== "Video" && (
                <div className="relative w-full h-64 md:h-80 lg:h-96 bg-gray-50 dark:bg-gray-900">
                  <img
                    alt={title || "Resource Thumbnail"}
                    className="object-contain w-full h-full"
                    src={thumbnailUrl}
                  />
                </div>
              )}

              {/* Header Content Section */}
              <div className={`px-6 pb-6 ${thumbnailUrl ? "pt-6" : "pt-6"}`}>
                {/* Title and Actions Row */}
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">
                      {title}
                    </h1>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShowIfHasAccess requiredRoles={["ROLE_SUPER_ADMIN"]}>
                      <Button
                        className="h-9 w-9"
                        onClick={() =>
                          window.open(
                            `/admin/resources/${resource.uuid}/edit`,
                            "_blank",
                          )
                        }
                        size="icon"
                        title="Edit resource in new tab"
                        variant="ghost"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </ShowIfHasAccess>
                    <FavoriteButton
                      isFavorited={isFavorited}
                      onToggle={handleToggleFavorite}
                    />
                    {resource.uuid && (
                      <ShowIfHasAccess
                        requiredRoles={["ROLE_SUPER_ADMIN", "ROLE_COACH"]}
                      >
                        <ShareToEmployeeButton
                          resourceTitle={title || "Resource"}
                          resourceUuid={resource.uuid}
                        />
                      </ShowIfHasAccess>
                    )}
                  </div>
                </div>

                {/* Metadata Row */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <div
                    className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: backgroundColor ?? "#ccc",
                      color: textColor ?? "#000",
                      border: borderColor ? `1px solid ${borderColor}` : "none",
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: icon ?? "" }} />
                    <span>{typeName}</span>
                  </div>
                  <span className="flex items-center gap-1">
                    <Eye size={16} />
                    {viewCount || 0} views
                  </span>
                  {publishStartDate && (
                    <span className="flex items-center gap-1">
                      <Calendar size={16} />
                      {new Date(publishStartDate).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Tags Section */}
                <div className="space-y-3">
                  {categories && categories.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Categories:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                          <Badge
                            className="text-xs"
                            key={category.id}
                            variant="outline"
                          >
                            {category.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {trades && trades.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Trades:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {trades.map((trade) => (
                          <Badge
                            className="text-xs"
                            key={trade.id}
                            variant="secondary"
                          >
                            {trade.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {roles && roles.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Job Title:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {roles.map((role) => (
                          <Badge
                            className="text-xs bg-blue-100 hover:bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            key={role.id}
                            variant="secondary"
                          >
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 pb-6">
                {/* Video Resource */}
                {typeName === "Video" && contentUrl && (
                  <div className="mb-8">
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                      <VideoEmbed contentUrl={contentUrl} />
                    </div>
                  </div>
                )}

                {/* Podcast Resource */}
                {typeName === "Podcast" && contentUrl && (
                  <div className="mb-8">
                    <PodcastEmbed contentUrl={contentUrl} />
                  </div>
                )}

                {/* File Resource - using FileEmbed component */}
                {typeName === "File" && resource?.file && (
                  <FileEmbed
                    bypassIframeForPdf={true}
                    contentUrl={resource.file.url || ""}
                    fileSize={resource.file.size}
                    mimeType={resource.file.mimeType}
                    title={resource.file.name}
                  />
                )}

                {typeName === "Series" &&
                  resource?.seriesEntries &&
                  resource.seriesEntries.length > 0 && (
                    <div className="space-y-8">
                      {resource.seriesEntries.map((entry, index) => (
                        <div className="space-y-4" key={entry.id}>
                          <div>
                            <h3 className="text-xl font-semibold">
                              {entry.title}
                            </h3>
                          </div>

                          {/* Render Video or Podcast embed based on type */}
                          {entry.resourceType === "Video" &&
                            entry.contentUrl && (
                              <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                                <VideoEmbed contentUrl={entry.contentUrl} />
                              </div>
                            )}

                          {entry.resourceType === "Podcast" &&
                            entry.contentUrl && (
                              <PodcastEmbed contentUrl={entry.contentUrl} />
                            )}

                          {/* Link to individual resource */}
                          <div className="text-sm">
                            <Link
                              className="text-primary hover:underline inline-flex items-center gap-1"
                              to={`/hub/resources/${entry.slug}`}
                            >
                              Full resource page
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                {/* Content Blocks */}
                {contentBlocks && contentBlocks.length > 0 && (
                  <div
                    className={`space-y-6 ${typeName === "Series" && resource?.seriesEntries && resource.seriesEntries.length > 0 ? "mt-8" : ""}`}
                  >
                    {contentBlocks.map((block, index) => (
                      <ResourceDetailsContentBlock
                        block={block}
                        key={block.id || index}
                      />
                    ))}
                  </div>
                )}

                {/* No content message */}
                {!contentUrl &&
                  (!contentBlocks || contentBlocks.length === 0) &&
                  typeName !== "File" &&
                  typeName !== "Series" && (
                    <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                      <p>No detailed content available for this resource.</p>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">
                Recommended Resources
              </h3>
              {relatedResources && relatedResources.length > 0 ? (
                <div className="space-y-4">
                  {relatedResources.map((rel, idx) => (
                    <div
                      className="flex items-center gap-4 border-b last:border-0 pb-4 mb-4 last:pb-0 last:mb-0"
                      key={idx}
                    >
                      <div className="relative w-24 shrink-0 rounded overflow-hidden">
                        <div className="relative aspect-video">
                          {rel.thumbnailUrl ? (
                            <img
                              alt={rel.title}
                              className="object-cover w-full h-full absolute inset-0"
                              src={rel.thumbnailUrl}
                            />
                          ) : (
                            <div className="w-full h-full absolute inset-0 rounded flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                              <span
                                className="inline-block scale-75"
                                dangerouslySetInnerHTML={{
                                  __html: rel.primaryIcon ?? "",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <a
                          href={`/hub/resources/${rel.slug}`}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          <p className="font-medium hover:underline">
                            {rel.title}
                          </p>
                        </a>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {rel.resourceType} •{" "}
                          {new Date(
                            rel.createdOrPublishStartDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No recommended resources found.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
}
